import { z } from 'zod';
import { EXIT_KEYS, EXIT_SCOPES } from '../enums';
import { decimalInRange, decimalStringSchema, jsonObjectSchema, uuidSchema } from './common';

/** Exit-strategy configuration (API_DOCS.md — Exit Strategies, ARCHITECTURE.md §9.3). */

export const exitConfigSchema = z.object({
  id: uuidSchema,
  exitKey: z.enum(EXIT_KEYS),
  scope: z.enum(EXIT_SCOPES),
  isDefault: z.boolean(),
  params: jsonObjectSchema,
  /** JSON Schema of the exit's params — renders the dashboard form. */
  paramSchema: jsonObjectSchema.nullable().optional(),
  updatedAt: z.string().datetime(),
});
export type ExitConfigDto = z.infer<typeof exitConfigSchema>;

export const exitParamsUpdateSchema = z.object({ params: jsonObjectSchema });
export type ExitParamsUpdate = z.infer<typeof exitParamsUpdateSchema>;

export const positionExitOverrideSchema = z.object({ exitConfigId: uuidSchema });
export type PositionExitOverride = z.infer<typeof positionExitOverrideSchema>;

/**
 * Canonical param shapes per exit key. The strategies themselves land in Phase 2, but the
 * shapes are shared now so the seed rows, the API and the dashboard agree from day one.
 *
 * Every exit carries `maxHoldMin`: PositionManager closes the trade with reason 'time' once
 * it has been open that long and the exit itself did not act on that tick. null = no limit.
 */
const maxHoldMin = z.number().int().min(5).max(10_080).nullable().default(null);
export const trailingStopParamsSchema = z.object({
  /** Distance of the trailing stop below the high-water price, in percent. */
  trailPct: decimalStringSchema,
  /** Optional ATR-based ratchet multiplier; the wider of the two stops wins. */
  ratchetAtrMult: decimalStringSchema.nullable().default(null),
  /** Trailing only arms once price is this far above entry, in percent. */
  activationPct: decimalStringSchema.default('0'),
  maxHoldMin,
});
export type TrailingStopParams = z.infer<typeof trailingStopParamsSchema>;

export const fixedTpSlParamsSchema = z.object({
  tpPct: decimalStringSchema,
  slPct: decimalStringSchema,
  maxHoldMin,
});
export type FixedTpSlParams = z.infer<typeof fixedTpSlParamsSchema>;

export const scaleOutParamsSchema = z.object({
  /** First take-profit trigger, in percent above entry. */
  tp1Pct: decimalStringSchema,
  /** Share of the position sold at tp1, in percent of the original quantity. */
  tp1SizePct: decimalStringSchema,
  /**
   * Trailing distance below the high-water price, in percent. Active from the moment the
   * position opens — leaving the first tranche unprotected until tp1 would mean unbounded
   * downside — and it keeps protecting the remainder after tp1 is taken.
   */
  trailPct: decimalStringSchema,
  maxHoldMin,
});
export type ScaleOutParams = z.infer<typeof scaleOutParamsSchema>;

/**
 * For strategies that decide their own exit (trend follower): only a catastrophe stop, set
 * once at entry — normal ups and downs are left to the strategy's daily signal.
 */
export const trendHoldParamsSchema = z.object({
  slPct: decimalStringSchema.refine(decimalInRange('1', '90'), 'must be between 1 and 90').default('25'),
  maxHoldMin,
});
export type TrendHoldParams = z.infer<typeof trendHoldParamsSchema>;

export const EXIT_PARAM_SCHEMAS = {
  trend_hold: trendHoldParamsSchema,
  trailing_stop: trailingStopParamsSchema,
  fixed_tpsl: fixedTpSlParamsSchema,
  scale_out: scaleOutParamsSchema,
} as const;
